/* Vind mijn deo / Find my deo quiz. Ported from ~/nuud-home/quiz.js (prototype v3).
   Everything store-specific (copy, product handles, reviews, Klaviyo list) comes
   from the JSON in snippets/nuud-quiz-config.liquid, so .com and .nl share this file.
   Prices, stock and variant ids are read live from /products/<handle>.js.
   Answers go to Klaviyo (profile properties + "Quiz Completed") and onto the cart
   as attributes. Ad pixels only ever get "the quiz was finished", never answers. */
(() => {
  const root = document.querySelector('[data-nq]');
  if (!root) return;
  const CFG = JSON.parse(root.querySelector('[data-nq-config]').textContent);
  const T = CFG.copy;
  const VERSION = 'nq-1-2026-10-01';

  // ------------------------------------------------------------ helpers
  const esc = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  const fill = (s, o) => String(s).replace(/\{(\w+)\}/g, (m, k) => (o[k] != null ? o[k] : m));
  // Shopify Markets can serve a visitor's own currency; shop.money_format only
  // knows the shop currency, so anything else goes through Intl.
  const money = cents => {
    if (CFG.currency && CFG.shopCurrency && CFG.currency !== CFG.shopCurrency) {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: CFG.currency }).format(cents / 100); } catch (e) {}
    }
    const n = (cents / 100).toFixed(2);
    const [w, d] = n.split('.');
    const tf = (sep, dec) => w.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + dec + d;
    return CFG.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) =>
      k === 'amount_with_comma_separator' ? tf('.', ',') : k === 'amount_no_decimals' ? w : tf(',', '.'));
  };
  const track = (name, params = {}) => {
    try { window.gtag && window.gtag('event', name, params); } catch (e) {}
    try { (window.dataLayer = window.dataLayer || []).push({ event: name, ...params }); } catch (e) {}
  };

  // ------------------------------------------------------------ products (live)
  const P = {};
  const productsReady = Promise.all(Object.entries(CFG.handles).map(([key, handle]) =>
    fetch(`${CFG.root}products/${handle}.js`).then(r => (r.ok ? r.json() : null)).then(p => {
      if (!p) return;
      const v = p.variants.find(x => x.available) || p.variants[0];
      P[key] = {
        key, handle, title: p.title, url: p.url, variantId: v.id,
        price: v.price, compareAt: v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : null,
        available: p.available && v.available && !p.requires_selling_plan,
        image: p.featured_image ? (p.featured_image.startsWith('//') ? 'https:' + p.featured_image : p.featured_image) : null,
      };
    }).catch(() => {})));
  const ok = k => P[k] && P[k].available;
  const img = (src, w) => src ? `${src}${src.includes('?') ? '&' : '?'}width=${w}` : '';

  // ------------------------------------------------------------ state
  let a = {};
  let step = 0;
  let forced = null;
  let advancing = false;
  let lastStep = -1;
  const R = () => a.reasons || [];
  const has = v => R().includes(v);
  const name = () => (a.name || '').trim();
  const sk = () => a.scent || null;

  // ------------------------------------------------------------ steps
  const FOLLOW_IDS = { performance: 'giveup', skin: 'symptoms', health: 'avoid', sustainability: 'green', convenience: 'easy' };
  const MULTI = { symptoms: true, avoid: true, green: true };
  const REASONS = ['performance', 'skin', 'health', 'sustainability', 'convenience'];
  const STEPS = [
    { id: 'reason', kind: 'hero' },
    { id: 'name', kind: 'text', fact: () => review('name') },
    { id: 'current', kind: 'multi-follow', fact: () => T.current.fact },
    { id: 'format', kind: 'cards', fact: () => T.format.fact },
    { id: 'scent', kind: 'choice', when: () => a.format === 'stick', fact: () => T.scent.fact },
    { id: 'email', kind: 'email', fact: () => review('email') },
    { id: 'matching', kind: 'matching', fact: () => T.matching.fact },
    { id: 'result', kind: 'result' },
  ];
  const visible = () => STEPS.filter(s => !s.when || s.when());
  const cur = () => visible()[step];

  // ------------------------------------------------------------ reviews
  // Picked fresh from the answers each time, so going back never leaves a
  // mismatched quote, and no review shows twice in one run.
  const RV = CFG.reviews;
  function reviewPlan() {
    const used = [];
    const pick = (...lists) => {
      for (const l of lists) for (const id of l || []) if (RV.items[id] && !used.includes(id)) { used.push(id); return RV.items[id]; }
      return null;
    };
    const extra = [
      ...(a.symptoms || []).map(v => RV.byAnswer['symptoms:' + v]),
      a.easy && RV.byAnswer['easy:' + a.easy],
    ].filter(Boolean).flat();
    const name = pick(RV.byReason[a.reason], RV.neutral);
    if (a.format === 'stick') {
      // follow the product actually shown: a sold-out scent falls back to the unscented stick
      const rr = recommend();
      const shownScent = rr.key === 'trio' ? 'mix' : rr.scent;
      const scented = !!shownScent;
      const fit = l => (l || []).filter(id => scented || !RV.scentedOnly.includes(id));
      const byReason = v => fit(RV.stickByReason[v]);
      const email = pick(byReason(a.reason), ...R().slice(1).map(byReason), fit(RV.stickFallback));
      const result = pick(scented ? RV.stickByScent[shownScent] : null, byReason(a.reason), ...R().slice(1).map(byReason), fit(RV.stickFallback), RV.neutral);
      return { name, email, result };
    }
    const fit = l => (a.format === 'both' ? (l || []).filter(id => RV.neutral.includes(id)) : l);
    const email = pick(fit(RV.byCurrent[a.current]), fit(RV.byReason[R()[1]]), fit(RV.neutral));
    const result = pick(fit(extra), fit(RV.byReason[a.reason]), ...R().slice(1).map(v => fit(RV.byReason[v])), RV.neutral);
    return { name, email, result };
  }
  const reviewLabel = r => (r.by ? fill(T.reviewNamed, { name: r.by }) : T.reviewAnon);
  const review = slot => {
    const r = reviewPlan()[slot];
    return r && { review: true, text: `“${r.text}”`, title: `★★★★★ ${reviewLabel(r)}` };
  };

  // ------------------------------------------------------------ recommendation
  const TIERS = [1, 2, 4];
  function keyFor(fmt, tier) {
    if (fmt === 'cream') return { 1: 'starter', 2: 'smarter', 4: 'family' }[tier];
    if (fmt === 'both') return { 1: 'comboS', 2: 'comboM', 4: 'comboF' }[tier];
    const s = sk();
    if (s === 'mix' && ok('trio')) return 'trio';
    // a scent that is sold out in every pack size falls back to the unscented stick,
    // so the pack picker never lists three sold-out rows
    const scented = s && !['none', 'mix'].includes(s) && TIERS.some(t => ok(`${s}${t === 1 ? '' : t}`));
    const base = scented ? s : 'stick';
    return `${base}${tier === 1 ? '' : tier}`;
  }
  function recommend() {
    const fmt = a.format || 'cream';
    let tier = a.pack || 1;
    let key = keyFor(fmt, tier);
    // never show a dead product: same format, nearest pack that is in stock
    if (!ok(key) && key !== 'trio') { const t = TIERS.find(x => ok(keyFor(fmt, x))); if (t) { tier = t; key = keyFor(fmt, t); } }
    if (!ok(key) && fmt === 'stick') key = ['stick', 'stick2', 'stick4'].find(ok) || key; // e.g. a sold-out scent
    if (!ok(key)) key = ['starter', 'smarter', 'comboS'].find(ok) || key;
    let alt = fmt === 'both' ? 'starter' : 'comboS';
    if (forced && ok(forced)) { alt = key; key = forced; }
    const f = key.startsWith('combo') ? 'both' : ['starter', 'smarter', 'family'].includes(key) ? 'cream' : 'stick';
    const scent = !forced && f === 'stick' && sk() && !['none', 'mix'].includes(sk()) && key.startsWith(sk()) ? sk() : null;
    return { key, prod: P[key], alt: ok(alt) && alt !== key ? P[alt] : null, fmt: f, scent, tier, packable: key !== 'trio' && !forced };
  }

  function reasons(r) {
    const W = T.result.why;
    const cream = r.fmt !== 'stick';
    const out = [];
    if (a.current === 'antiperspirant') out.push(W.antiperspirant);
    if (a.current === 'natural' && cream && !has('skin')) out.push(W.naturalBefore);
    R().forEach(v => { const m = W[v]; if (m) out.push(cream ? m.cream : m.stick); });
    if (a.giveup === 'sport') out.push(cream ? W.sport.cream : W.sport.stick);
    const avoid = a.avoid || [];
    if (cream && a.current !== 'natural' && avoid.some(x => ['alcohol', 'bakingsoda', 'perfume'].includes(x))) out.push(W.avoidCream);
    if (avoid.includes('synthetic')) out.push(W.synthetic);
    const green = a.green || [];
    if (green.includes('vegan')) out.push(W.vegan);
    if (green.includes('organic') && cream) out.push(W.organic);
    if (green.includes('lessbuying')) out.push(W.lessbuying);
    if (a.easy === 'runout') out.push(W.runout);
    if (a.easy === 'notdaily' && cream && !has('convenience')) out.push(W.notdaily);
    if ((a.symptoms || []).includes('shave')) out.push(W.shave);
    if (has('skin') && r.scent) out.push(W.scentSkin);
    const ins = T.result.inside[r.key];
    if (ins) out.push(fill(T.result.insideLine, { inside: ins.inside, lasts: ins.lasts }));
    return out.filter(Boolean);
  }

  // One line at the top of each question that answers back to the previous one.
  function carry(s) {
    const prev = visible()[step - 1];
    if (!prev || s.kind === 'result') return '';
    const C = T.carry;
    switch (prev.id) {
      case 'current': {
        const b = a.brand && !['other', 'unknown'].includes(a.brand) ? a.brand : null;
        if (b && a.current !== 'antiperspirant') return fill(C.brand, { b });
        return (b && a.current === 'antiperspirant' ? fill(C.brand, { b }) + ' ' : '') + (C.current[a.current] || '');
      }
      case 'name': return name() ? fill(C.named, { n: esc(name()) }) : C.anon;
      case 'format': return C.format[a.format] || '';
      case 'scent': return C.scent[sk()] || '';
      case 'reason': {
        if (a.giveup === 'lunch') return C.lunch;
        if (a.giveup === 'sport') return C.sport;
        if ((a.symptoms || []).length && !(a.symptoms || []).includes('careful')) return C.symptoms;
        if (a.easy === 'runout') return C.runout;
        if (a.easy === 'notdaily') return C.notdaily;
        if ((a.avoid || []).length) return C.avoid;
        const names = R().map(v => C.labels[v]);
        if (names.length > 1) return fill(C.multi, { list: names.slice(0, -1).join(', ') + C.and + names[names.length - 1] });
        return C.single[a.reason] || '';
      }
      default: return '';
    }
  }

  // ------------------------------------------------------------ dom
  const $ = s => root.querySelector(s);
  const stage = $('#qz-stage');
  const nav = $('#qz-nav');
  const next = $('#qz-next');
  const fact = $('#qz-fact');
  const factTitle = $('#qz-fact-title');
  const factText = $('#qz-fact-text');
  const bar = $('.qz-bar span');
  const headBack = $('.qz-back');
  $('#qz-nav-back').textContent = T.back;

  function setFact(v) {
    const val = typeof v === 'function' ? v() : v;
    const text = val && typeof val === 'object' ? val.text : val;
    factTitle.textContent = val && val.review ? val.title : T.factTitle;
    fact.hidden = !text;
    if (text && factText.textContent !== text) {
      factText.textContent = text;
      fact.classList.remove('is-in'); void fact.offsetWidth; fact.classList.add('is-in');
    }
  }
  function progress() {
    // fast early progress keeps people going (Villar et al. 2013): a sliver on
    // screen one, 25% after the first answer, 100% at the email step
    const qs = visible().filter(x => !['result', 'matching'].includes(x.kind));
    const last = qs.findIndex(x => x.kind === 'email');
    const w = step === 0 ? 6 : step >= last ? 100 : 25 + 75 * (step - 1) / Math.max(1, last - 1);
    bar.style.width = `${Math.min(100, w)}%`;
  }
  function go(delta) {
    step = Math.max(0, Math.min(visible().length - 1, step + delta));
    if (delta < 0 && cur().kind === 'matching') step -= 1;
    forced = null;
    render();
    root.scrollIntoView({ block: 'start' });
    track('quiz_step', { quiz_step: cur().id });
  }
  const head = (s, title) => { const c = carry(s); return `${c ? `<p class="qz-carry">${c}</p>` : ''}<h1 class="qz-title">${title}</h1>`; };
  const ICONS = {
    cream: `<svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 44 L36 14 a4 4 0 0 1 7 3 L33 47"/><path d="M22 44 l11 3 -2 6 -11 -3z"/><circle cx="48" cy="42" r="3.2" fill="currentColor" stroke="none"/><path d="M44 50 q4 3 8 0"/></svg>`,
    stick: `<svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="18" y="20" width="16" height="30" rx="4"/><path d="M18 28 h16"/><path d="M20 20 q6 -6 12 0"/><path d="M40 22 q8 10 0 20" opacity=".55"/><path d="M46 18 q10 14 0 28" opacity=".3"/></svg>`,
    both: `<svg viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="12" y="22" width="14" height="26" rx="4"/><path d="M12 29 h14"/><path d="M36 46 L44 22 a3.5 3.5 0 0 1 6 2 L44 48"/><path d="M36 46 l8 2 -1.5 5 -8 -2z"/></svg>`,
  };
  const shot = (src, alt, w = 600) => `<span class="qz-shot">${src ? `<img src="${esc(img(src, w))}" alt="${esc(alt)}" loading="lazy">` : ''}</span>`;
  function choiceHTML(c, checked, role = 'radio') {
    return `<button type="button" role="${role}" class="qz-choice" data-v="${esc(c.v)}" aria-checked="${checked}">
      ${c.icon ? `<span class="qz-icon">${ICONS[c.icon]}</span>` : ''}
      <span class="qz-choice__label">${c.label}</span>
      ${c.note ? `<span class="qz-choice__note">${c.note}</span>` : ''}
      ${c.points ? `<ul class="qz-points">${c.points.map(t => `<li>${t}</li>`).join('')}</ul>` : ''}</button>`;
  }

  // ------------------------------------------------------------ render
  function render() {
    const s = cur();
    if (lastStep !== step) { stage.classList.remove('is-in'); void stage.offsetWidth; stage.classList.add('is-in'); lastStep = step; }
    root.dataset.kind = s.kind;
    headBack.hidden = step === 0 || ['result', 'matching'].includes(s.kind);
    nav.hidden = ['hero', 'result', 'matching'].includes(s.kind);
    next.innerHTML = `${T.next} &rarr;`;
    next.disabled = false;
    progress();
    ({ hero: renderHero, choice: renderChoice, cards: renderChoice, text: renderText,
       'multi-follow': renderCurrent, email: renderEmail, matching: renderMatching, result: renderResult })[s.kind](s);
    setFact(['result', 'hero'].includes(s.kind) ? null : s.fact);
  }

  function renderHero() {
    const Q = T.reason;
    const follow = v => {
      const id = FOLLOW_IDS[v], F = Q.follow[id];
      if (!F) return '';
      const fv = a[id];
      return `<div class="qz-follow" data-for="${v}">
        <p class="qz-follow__q">${F.q} <span class="qz-optional">${T.optional}</span></p>
        <div class="qz-pills" role="${MULTI[id] ? 'group' : 'radiogroup'}">
          ${Object.entries(F.choices).map(([k, label]) => `<button type="button" class="qz-pill" data-f="${v}" role="${MULTI[id] ? 'checkbox' : 'radio'}" data-v="${k}"
            aria-checked="${MULTI[id] ? (fv || []).includes(k) : fv === k}">${label}</button>`).join('')}
        </div>
        <p class="qz-react">${Q.choices[v].react}</p></div>`;
    };
    stage.innerHTML = `
      <section class="qz-hero">
        <div class="qz-hero__copy">
          <h1 class="qz-title qz-title--hero">${T.hero.title}</h1>
          <p class="qz-hero__lede">${T.hero.lede}</p>
          <p class="qz-proof">${T.hero.proof}</p>
          <div class="qz-hero__card">
            <p class="qz-hero__q"><span>${T.hero.start}</span> ${Q.title}</p>
            <p class="qz-hero__hint">${Q.hint}</p>
            <div class="qz-choices qz-choices--compact" role="group">
              ${REASONS.map(v => choiceHTML({ v, ...Q.choices[v] }, has(v), 'checkbox')
                + (R().indexOf(v) > -1 && R().indexOf(v) < 2 ? follow(v) : '')).join('')}
            </div>
            <button class="qz-cta qz-cta--wide" type="button" id="qz-hero-go" ${R().length ? '' : 'disabled'}>${T.next} &rarr;</button>
          </div>
        </div>
        <div class="qz-hero__media">${CFG.heroImage ? shot(CFG.heroImage, '', 900) : ''}</div>
      </section>`;
    const keep = fn => { const y = window.scrollY; fn(); render(); window.scrollTo(0, y); };
    stage.querySelectorAll('.qz-choice').forEach(b => b.onclick = () => keep(() => {
      const v = b.dataset.v;
      if (has(v)) { a.reasons = R().filter(x => x !== v); delete a[FOLLOW_IDS[v]]; }
      else a.reasons = [...R(), v];
      a.reason = R()[0];
    }));
    stage.querySelectorAll('.qz-pill').forEach(p => p.onclick = () => keep(() => {
      const id = FOLLOW_IDS[p.dataset.f], v = p.dataset.v;
      if (MULTI[id]) {
        const arr = a[id] || [];
        a[id] = arr.includes(v) ? arr.filter(x => x !== v) : v === 'careful' ? ['careful'] : [...arr.filter(x => x !== 'careful'), v];
      } else a[id] = a[id] === v ? undefined : v; // tap again to clear: follow-ups are optional
    }));
    $('#qz-hero-go').onclick = () => { track('quiz_start', { quiz_reasons: R().join(',') }); go(1); };
  }

  function renderMatching() {
    const M = T.matching.lines;
    const lines = [
      ...R().slice(0, 2).map(v => M[v]),
      M[a.format || 'cream'],
      sk() ? M[sk()] : null,
    ].filter(Boolean);
    stage.innerHTML = `
      <section class="qz-q qz-matching">
        <div class="qz-spinner" aria-hidden="true"></div>
        <h1 class="qz-title">${T.matching.title}</h1>
        <ul class="qz-checks">${lines.map((l, i) => `<li style="animation-delay:${150 + i * 250}ms">${l}</li>`).join('')}</ul>
      </section>`;
    // wait for the live product data too, so the result never renders empty
    const min = new Promise(r => setTimeout(r, 450 + lines.length * 250));
    Promise.all([min, productsReady]).then(() => { if (cur().kind === 'matching') go(1); });
  }

  function renderChoice(s) {
    const Q = T[s.id];
    const choices = Object.entries(Q.choices).map(([v, c]) => ({ v, icon: s.id === 'format' ? v : null, ...c }));
    stage.innerHTML = `
      <section class="qz-q">
        ${head(s, Q.title)}
        ${Q.sub ? `<p class="qz-sub">${Q.sub}</p>` : ''}
        <div class="qz-choices ${s.kind === 'cards' ? 'qz-choices--cards' : ''}" role="radiogroup">
          ${choices.map(c => choiceHTML(c, a[s.id] === c.v)).join('')}
        </div>
      </section>`;
    next.disabled = !a[s.id];
    stage.querySelectorAll('.qz-choice').forEach(b => b.onclick = () => {
      if (advancing) return; // a double tap must not skip the next question
      advancing = true;
      a[s.id] = b.dataset.v;
      if (s.id === 'format' && b.dataset.v !== 'stick') delete a.scent;
      stage.querySelectorAll('.qz-choice').forEach(x => x.setAttribute('aria-checked', x === b));
      setTimeout(() => { advancing = false; go(1); }, 220);
    });
    next.onclick = () => go(1);
  }

  function renderText(s) {
    const Q = T.name;
    stage.innerHTML = `
      <section class="qz-q">
        ${head(s, Q.title)}
        <p class="qz-sub">${Q.sub}</p>
        <form class="qz-textq" id="qz-tq">
          <input class="qz-input qz-input--big" id="qz-tq-in" type="text" autocomplete="given-name" placeholder="${esc(Q.placeholder)}" value="${esc(a.name)}" maxlength="40">
        </form>
        <button type="button" class="qz-link qz-skip" id="qz-tq-skip">${Q.skip}</button>
      </section>`;
    const inp = $('#qz-tq-in');
    const sync = () => { a.name = inp.value.trim(); next.disabled = !a.name; };
    inp.oninput = sync; sync();
    setTimeout(() => inp.focus({ preventScroll: true }), 50);
    $('#qz-tq').onsubmit = e => { e.preventDefault(); if (a.name) go(1); };
    $('#qz-tq-skip').onclick = () => { a.name = ''; go(1); };
    next.onclick = () => go(1);
  }

  // what they use now, with a brand follow-up right under the chosen answer
  function renderCurrent(s) {
    const Q = T.current;
    const brands = v => {
      const B = CFG.brands[v];
      return B && { q: Q.which, choices: [...B.map(b => [b, b]), ['other', Q.other], ['unknown', Q.unknown]] };
    };
    const f = a.current && brands(a.current);
    stage.innerHTML = `
      <section class="qz-q">
        ${head(s, name() ? fill(Q.titleNamed, { n: esc(name()) }) : Q.title)}
        <div class="qz-choices" role="radiogroup">
          ${Object.entries(Q.choices).map(([v, label]) => choiceHTML({ v, label }, a.current === v) + (f && a.current === v ? `<div class="qz-follow">
            <p class="qz-follow__q">${f.q}</p>
            <div class="qz-pills" role="radiogroup">
              ${f.choices.map(([k, label]) => `<button type="button" class="qz-pill" role="radio" data-v="${esc(k)}" aria-checked="${a.brand === k}">${esc(label)}</button>`).join('')}
            </div></div>` : '')).join('')}
        </div>
      </section>`;
    next.disabled = !a.current || (f && !a.brand);
    const advance = () => { if (advancing) return; advancing = true; setTimeout(() => { advancing = false; go(1); }, 240); };
    stage.querySelectorAll('.qz-choice').forEach(b => b.onclick = () => {
      if (advancing) return;
      if (a.current !== b.dataset.v) delete a.brand;
      a.current = b.dataset.v;
      if (!brands(a.current)) { stage.querySelectorAll('.qz-choice').forEach(x => x.setAttribute('aria-checked', x === b)); advance(); return; }
      const y = window.scrollY; render(); window.scrollTo(0, y);
      stage.querySelector('.qz-follow')?.scrollIntoView({ block: 'center' });
    });
    stage.querySelectorAll('.qz-pill').forEach(p => p.onclick = () => {
      a.brand = p.dataset.v;
      stage.querySelectorAll('.qz-pill').forEach(x => x.setAttribute('aria-checked', x === p));
      advance();
    });
    next.onclick = () => go(1);
  }

  function renderEmail(s) {
    const Q = T.email;
    stage.innerHTML = `
      <section class="qz-q qz-email">
        <p class="qz-carry">${Q.carry}</p>
        <h1 class="qz-title">${Q.title}</h1>
        <p class="qz-sub">${Q.sub}</p>
        <form class="qz-emailform" id="qz-ef" novalidate>
          ${a.name ? '' : `<label class="qz-field"><span>${Q.nameLabel}</span>
            <input class="qz-input" id="qz-ef-name" type="text" autocomplete="given-name" placeholder="${esc(Q.nameLabel)}"></label>`}
          <label class="qz-field"><span>${Q.emailLabel}</span>
            <input class="qz-input" id="qz-ef-email" type="email" autocomplete="email" value="${esc(a.email)}" placeholder="${esc(Q.placeholder)}" required></label>
          <label class="qz-consent">
            <input type="checkbox" id="qz-ef-optin" ${a.optin !== false ? 'checked' : ''}>
            <span>${Q.consent}</span>
          </label>
          <p class="qz-error" id="qz-ef-err" hidden>${Q.error}</p>
        </form>
      </section>`;
    next.innerHTML = `${Q.cta} &rarr;`;
    const em = $('#qz-ef-email');
    const valid = () => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(em.value.trim());
    em.oninput = () => { $('#qz-ef-err').hidden = true; next.disabled = !valid(); };
    next.disabled = !valid();
    const submit = () => {
      if (!valid()) { $('#qz-ef-err').hidden = false; return; }
      a.email = em.value.trim();
      a.name = $('#qz-ef-name')?.value.trim() || a.name;
      a.optin = $('#qz-ef-optin').checked;
      productsReady.then(sendToKlaviyo);
      track('quiz_lead', {});
      try { window.Shopify && Shopify.analytics && Shopify.analytics.publish('quiz_lead', {}); } catch (e) {}
      go(1);
    };
    $('#qz-ef').onsubmit = e => { e.preventDefault(); submit(); };
    next.onclick = submit;
    setTimeout(() => (a.name ? em : $('#qz-ef-name')).focus({ preventScroll: true }), 50);
  }

  // ------------------------------------------------------------ klaviyo (client API, public key only)
  function quizProps() {
    const r = recommend();
    return {
      quiz_completed_at: new Date().toISOString().slice(0, 10),
      quiz_version: VERSION,
      quiz_store: CFG.store,
      quiz_reasons: R(),
      quiz_reason_primary: a.reason || null,
      quiz_current_product: a.current || null,
      quiz_current_brand: a.brand || null,
      quiz_format: a.format || null,
      quiz_scent: sk(),
      quiz_giveup: a.giveup || null,
      quiz_skin_symptoms: a.symptoms || null,
      quiz_avoid: a.avoid || null,
      quiz_sustainability: a.green || null,
      quiz_convenience: a.easy || null,
      quiz_result_product: r.prod ? r.prod.title : null,
      quiz_result_handle: r.prod ? r.prod.handle : null,
      quiz_result_url: r.prod ? location.origin + r.prod.url : null,
      quiz_result_image: r.prod ? img(r.prod.image, 600) : null,
    };
  }
  function sendToKlaviyo() {
    const K = CFG.klaviyo;
    if (!K || !K.company) return;
    const props = Object.fromEntries(Object.entries(quizProps()).filter(([, v]) => v != null && !(Array.isArray(v) && !v.length)));
    const profile = { email: a.email, ...(name() ? { first_name: name() } : {}), properties: props };
    const post = (path, body) => fetch(`https://a.klaviyo.com/client/${path}/?company_id=${K.company}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', revision: '2025-10-15' }, body: JSON.stringify(body),
    }).catch(() => {});
    // 1. profile + answers (also when they unticked the box: no consent, but we keep the answers)
    post('profiles', { data: { type: 'profile', attributes: profile } });
    // 2. consent: the quiz's own list
    if (a.optin && K.list) post('subscriptions', { data: { type: 'subscription', attributes: {
      custom_source: K.source, profile: { data: { type: 'profile', attributes: {
        email: a.email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } } } } },
      relationships: { list: { data: { type: 'list', id: K.list } } } } });
    // 3. the event that starts the "your advice" flow
    post('events', { data: { type: 'event', attributes: {
      metric: { data: { type: 'metric', attributes: { name: 'Quiz Completed' } } },
      profile: { data: { type: 'profile', attributes: { email: a.email } } },
      properties: props, unique_id: `${a.email}-${Date.now()}` } } });
    // 4. tie this browser to the profile so later browsing counts
    try { window.klaviyo && window.klaviyo.identify && window.klaviyo.identify({ email: a.email }); } catch (e) {}
  }

  // ------------------------------------------------------------ result
  function renderResult() {
    const RS = T.result;
    const r = recommend();
    const p = r.prod;
    if (!p) { stage.innerHTML = `<section class="qz-q"><h1 class="qz-title">${RS.unavailable}</h1><a class="qz-cta" href="${CFG.root}pages/shop-all">${RS.shopAll}</a></section>`; return; }
    if (!a.addons) a.addons = [];
    const unit = r.fmt === 'stick' ? RS.units.stick : r.fmt === 'both' ? null : RS.units.tube;
    const packs = r.packable ? TIERS.map(t => {
      const key = keyFor(r.fmt, t), q = P[key];
      if (!q) return null;
      const label = unit ? `${t} ${t === 1 ? unit[0] : unit[1]}` : RS.combo[t];
      return { t, key, label, q, lasts: (RS.inside[key] || {}).lasts || '' };
    }).filter(Boolean) : [];
    const addonKeys = (r.fmt === 'stick' ? ['trycream'] : ['squeezy', 'timey']).filter(k => ok(k === 'trycream' ? 'starter' : k));
    const addonProd = k => P[k === 'trycream' ? 'starter' : k];
    const addTotal = a.addons.filter(x => addonKeys.includes(x)).reduce((t, x) => t + addonProd(x).price, 0);
    const total = p.price + addTotal;
    const chips = [
      RS.chips.current[a.current],
      ...R().map(v => RS.chips.reasons[v]),
      RS.chips.fmt[r.fmt],
    ].filter(Boolean);
    const rv = reviewPlan().result;
    const priceHTML = q => `${q.compareAt ? `<s>${money(q.compareAt)}</s>` : ''}${money(q.price)}`;
    stage.innerHTML = `
      <section class="qz-result">
        <div class="qz-result__media">${shot(p.image, p.title, 900)}</div>
        <div class="qz-result__body">
          <p class="qz-kicker">${name() ? fill(RS.kickerNamed, { n: esc(name()) }) : RS.kicker}</p>
          <h1 class="qz-title">${esc((RS.titles || {})[r.key] || p.title)}</h1>
          <p class="qz-resproof">${T.hero.proof}</p>
          <ul class="qz-chips">${chips.map(c => `<li>${c}</li>`).join('')}</ul>
          <ul class="qz-why">${reasons(r).map(t => `<li>${t}</li>`).join('')}</ul>
          ${rv ? `<blockquote class="qz-review"><p>“${esc(rv.text)}”</p><footer><span class="qz-stars" aria-label="5/5">★★★★★</span> ${esc(reviewLabel(rv))}</footer></blockquote>` : ''}
          ${packs.length > 1 ? `<fieldset class="qz-packs">
            <legend>${RS.howMuch}</legend>
            <div class="qz-packs__row">
              ${packs.map(x => `<label class="qz-pack">
                <input type="radio" name="qz-pack" value="${x.t}" ${r.tier === x.t ? 'checked' : ''} ${x.q.available ? '' : 'disabled'}>
                ${x.t === 1 ? `<span class="qz-pack__tag">${RS.mostBought}</span>` : ''}
                <span class="qz-pack__label">${x.label}</span>
                <span class="qz-pack__price">${x.q.available ? priceHTML(x.q) : RS.soldOut}</span>
                <span class="qz-pack__lasts">${x.lasts}</span>
              </label>`).join('')}
            </div></fieldset>` : ''}
          ${addonKeys.length ? `<fieldset class="qz-addons">
            <legend>${r.fmt === 'stick' ? RS.addonsStick : RS.addonsCream}</legend>
            ${addonKeys.map(k => { const q = addonProd(k); return `<label class="qz-addon">
              <input type="checkbox" value="${k}" ${a.addons.includes(k) ? 'checked' : ''}>
              ${shot(q.image, q.title, 160)}
              <span class="qz-addon__text"><strong>${esc(RS.addons[k].name || q.title)}</strong><small>${RS.addons[k].line}</small></span>
              <span class="qz-addon__price">+${priceHTML(q)}</span>
            </label>`; }).join('')}
          </fieldset>` : ''}
          ${a.email && RS.sent ? `<p class="qz-sent">${fill(RS.sent, { email: esc(a.email) })}</p>` : ''}
          <button class="qz-cta qz-cta--wide" type="button" id="qz-add">${RS.cta} · ${money(total)}</button>
          <p class="qz-cart-error" id="qz-cart-error" hidden>${RS.cartError}</p>
          ${r.alt ? `<p class="qz-alt">${forced ? RS.back : RS.alt}
            <button type="button" class="qz-link" id="qz-alt">${esc(r.alt.title)}, ${money(r.alt.price)}</button></p>` : ''}
          <button type="button" class="qz-link qz-restart" id="qz-restart">${RS.restart}</button>
        </div>
      </section>`;
    const keep = fn => () => { const y = window.scrollY; fn(); renderResult(); window.scrollTo(0, y); };
    stage.querySelectorAll('input[name=qz-pack]').forEach(i => i.onchange = keep(() => { a.pack = +i.value; }));
    stage.querySelectorAll('.qz-addon input').forEach(i => i.onchange = keep(() => {
      a.addons = i.checked ? [...a.addons, i.value] : a.addons.filter(x => x !== i.value);
    }));
    $('#qz-add').onclick = e => addToCart(e.currentTarget, r, addonKeys.filter(k => a.addons.includes(k)).map(addonProd));
    const altBtn = $('#qz-alt');
    if (altBtn) altBtn.onclick = keep(() => { forced = forced ? null : r.alt.key; });
    $('#qz-restart').onclick = () => { a = {}; step = 0; forced = null; render(); root.scrollIntoView({ block: 'start' }); };
    if (!renderResult.seen) {
      renderResult.seen = true;
      track('quiz_complete', { quiz_result: p.handle });
      try { window.Shopify && Shopify.analytics && Shopify.analytics.publish('quiz_completed', {}); } catch (e) {}
    }
  }

  // the answers ride along on the order as cart attributes
  function addToCart(btn, r, extras) {
    btn.disabled = true;
    btn.textContent = T.result.adding;
    const items = [r.prod, ...extras].map(q => ({ id: q.variantId, quantity: 1 }));
    const attrs = {
      quiz: VERSION,
      quiz_result: r.prod.handle,
      quiz_reasons: R().join(','),
      quiz_current: [a.current, a.brand].filter(Boolean).join(':'),
      quiz_format: a.format || '',
      quiz_scent: sk() || '',
    };
    const json = (url, body) => fetch(CFG.root + url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) })
      .then(res => { if (!res.ok) throw new Error(res.status); return res.json(); });
    json('cart/add.js', { items })
      .then(() => json('cart/update.js', { attributes: attrs }))
      .then(() => { track('quiz_add_to_cart', { quiz_result: r.prod.handle }); window.location.href = CFG.root + 'checkout'; })
      .catch(() => { btn.disabled = false; btn.textContent = T.result.cta; $('#qz-cart-error').hidden = false; });
  }

  // ------------------------------------------------------------ wiring
  // Shopify's preview bar (theme previews only) is fixed to the bottom of the
  // screen and would cover the nav; lift the nav and fact strip above it.
  const lift = () => {
    const bar = document.getElementById('PBarNextFrameWrapper') || document.getElementById('preview-bar-iframe');
    const h = bar ? Math.max(0, innerHeight - bar.getBoundingClientRect().top) : 0;
    root.style.setProperty('--qz-lift', `${h > innerHeight / 2 ? 0 : Math.round(h)}px`);
  };
  lift(); setTimeout(lift, 1500); setTimeout(lift, 4000); addEventListener('resize', lift);
  headBack.onclick = () => go(-1);
  $('#qz-nav-back').onclick = () => go(-1);
  render();
  track('quiz_view', {});
})();
